'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  Battery,
  Zap,
  Cog,
  Thermometer,
  Cpu,
  Activity,
  Shield,
  GraduationCap,
  Car,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Package,
  Building2,
  Phone,
  Wrench,
} from 'lucide-react';
import { partCategories, brandCategories } from '@/lib/site-data';

const iconMap: Record<string, typeof Battery> = {
  Battery,
  Zap,
  Cog,
  Thermometer,
  Cpu,
  Activity,
  Shield,
};

export default function ProductsPage() {
  const [activeTab, setActiveTab] = useState<'parts' | 'training'>('parts');
  const [activeBrand, setActiveBrand] = useState<string | null>(null);

  const selectedBrand = brandCategories.find((b) => b.slug === activeBrand);

  return (
    <main className="relative min-h-screen pt-24 pb-20">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--color-border)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)]/5 via-transparent to-[var(--color-accent)]/5" />
        <div className="absolute top-0 left-1/3 w-[500px] h-[250px] bg-[var(--color-primary)]/10 blur-[100px] rounded-full" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 text-[var(--color-primary)] text-sm font-medium mb-6">
              <Package size={16} />
              Parts Supply & Training
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
                Service Chinese EVs
              </span>
            </h1>
            <p className="text-lg text-[var(--color-muted)] mb-8 leading-relaxed">
              From battery modules to diagnostic tools — and from basic training to advanced reconditioning.
              We supply the parts and the know-how to help your workshop dominate the NEV after-sales market.
            </p>

            {/* Tab switcher */}
            <div className="inline-flex p-1.5 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)]">
              <button
                onClick={() => setActiveTab('parts')}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'parts'
                    ? 'bg-[var(--color-primary)] text-[var(--color-background)] shadow-lg shadow-[var(--color-primary)]/20'
                    : 'text-[var(--color-muted)] hover:text-[var(--color-foreground)]'
                }`}
              >
                <Package size={16} />
                Parts Supply
              </button>
              <button
                onClick={() => setActiveTab('training')}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'training'
                    ? 'bg-[var(--color-primary)] text-[var(--color-background)] shadow-lg shadow-[var(--color-primary)]/20'
                    : 'text-[var(--color-muted)] hover:text-[var(--color-foreground)]'
                }`}
              >
                <GraduationCap size={16} />
                Training Programs
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        {/* ===== PARTS TAB ===== */}
        {activeTab === 'parts' && (
          <>
            {/* Brand Section */}
            <section className="py-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Browse by{' '}
                  <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
                    Vehicle Brand
                  </span>
                </h2>
                <p className="text-[var(--color-muted)] max-w-2xl mx-auto">
                  We stock parts for all major Chinese EV brands currently on Middle East and Southeast Asian roads.
                  Can&apos;t find your brand? We can source it.
                </p>
              </div>

              {!activeBrand ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brandCategories.map((brand) => (
                    <button
                      key={brand.slug}
                      onClick={() => setActiveBrand(brand.slug)}
                      className="group text-left p-6 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:shadow-lg hover:shadow-[var(--color-primary)]/5 transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-accent)]/20 flex items-center justify-center">
                          <Car className="w-7 h-7 text-[var(--color-primary)]" />
                        </div>
                        <ChevronRight className="w-5 h-5 text-[var(--color-muted)] group-hover:text-[var(--color-primary)] group-hover:translate-x-1 transition-all" />
                      </div>
                      <h3 className="text-xl font-bold mb-1 group-hover:text-[var(--color-primary)] transition-colors">
                        {brand.name}
                      </h3>
                      <p className="text-sm text-[var(--color-muted)] mb-4">
                        {brand.fullName}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {brand.vehicleModels.slice(0, 3).map((model) => (
                          <span
                            key={model}
                            className="px-2 py-0.5 text-[11px] rounded-md bg-[var(--color-surface-light)] text-[var(--color-muted)]"
                          >
                            {model}
                          </span>
                        ))}
                        {brand.vehicleModels.length > 3 && (
                          <span className="px-2 py-0.5 text-[11px] rounded-md bg-[var(--color-surface-light)] text-[var(--color-muted)]">
                            +{brand.vehicleModels.length - 3} more
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                selectedBrand && (
                  <div className="animate-fadeIn">
                    <button
                      onClick={() => setActiveBrand(null)}
                      className="inline-flex items-center gap-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-primary)] transition-colors mb-6"
                    >
                      ← Back to all brands
                    </button>

                    <div className="grid lg:grid-cols-3 gap-8">
                      {/* Brand info */}
                      <div className="lg:col-span-1">
                        <div className="sticky top-28 p-6 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)]">
                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-accent)]/20 flex items-center justify-center mb-4">
                            <Building2 className="w-8 h-8 text-[var(--color-primary)]" />
                          </div>
                          <h3 className="text-2xl font-bold mb-1">{selectedBrand.name}</h3>
                          <p className="text-sm text-[var(--color-muted)] mb-4">{selectedBrand.fullName}</p>
                          <p className="text-sm text-[var(--color-muted)] mb-6 leading-relaxed">
                            {selectedBrand.description}
                          </p>
                          <div className="space-y-2 mb-6">
                            <p className="text-sm font-medium text-[var(--color-foreground)] mb-2">
                              Models we support:
                            </p>
                            {selectedBrand.vehicleModels.map((model) => (
                              <div key={model} className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
                                <CheckCircle2 size={14} className="text-[var(--color-primary)] flex-shrink-0" />
                                {model}
                              </div>
                            ))}
                          </div>
                          <Link
                            href="/contact"
                            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-[var(--color-background)] bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all"
                          >
                            <Phone size={16} />
                            Get a Quote
                          </Link>
                        </div>
                      </div>

                      {/* Parts for this brand */}
                      <div className="lg:col-span-2">
                        <h4 className="text-lg font-semibold mb-4">
                          Available parts for {selectedBrand.name}
                        </h4>
                        <div className="space-y-4">
                          {partCategories.map((cat) => {
                            const Icon = iconMap[cat.icon] || Package;
                            return (
                              <div
                                key={cat.slug}
                                className="p-5 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)]/30 transition-all"
                              >
                                <div className="flex items-start gap-4">
                                  <div className="w-10 h-10 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                                    <Icon size={20} className="text-[var(--color-primary)]" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h5 className="font-semibold mb-1">{cat.name}</h5>
                                    <p className="text-sm text-[var(--color-muted)] mb-3">{cat.description}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {cat.items.map((item) => (
                                        <span
                                          key={item}
                                          className="px-2 py-0.5 text-[11px] rounded-md bg-[var(--color-surface-light)] text-[var(--color-muted)]"
                                        >
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-[var(--color-primary)]/10 to-[var(--color-accent)]/10 border border-[var(--color-primary)]/20">
                          <h5 className="text-lg font-semibold mb-2">
                            Don&apos;t see the part you need?
                          </h5>
                          <p className="text-sm text-[var(--color-muted)] mb-4">
                            We source OEM and aftermarket parts directly from Chinese factories.
                            Send us your VIN or part number — we&apos;ll find it for you.
                          </p>
                          <Link
                            href="/contact"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-[var(--color-background)] bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all"
                          >
                            Request Specific Part
                            <ArrowRight size={16} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </section>

            {/* By Component Type Section */}
            <section className="py-16 border-t border-[var(--color-border)]">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Browse by{' '}
                  <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
                    Component Type
                  </span>
                </h2>
                <p className="text-[var(--color-muted)] max-w-2xl mx-auto">
                  Know what system you need parts for? Browse our comprehensive catalog by component category.
                  All parts available for bulk wholesale.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {partCategories.map((cat) => {
                  const Icon = iconMap[cat.icon] || Package;
                  return (
                    <div
                      key={cat.slug}
                      className="group p-6 rounded-2xl bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)]/30 hover:shadow-lg hover:shadow-[var(--color-primary)]/5 transition-all"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--color-primary)]/15 to-[var(--color-accent)]/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Icon size={24} className="text-[var(--color-primary)]" />
                      </div>
                      <h3 className="text-lg font-bold mb-2 group-hover:text-[var(--color-primary)] transition-colors">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-[var(--color-muted)] mb-4 leading-relaxed">
                        {cat.description}
                      </p>
                      <div className="space-y-1.5 mb-5">
                        {cat.items.slice(0, 4).map((item) => (
                          <div key={item} className="flex items-center gap-2 text-sm text-[var(--color-muted)]">
                            <CheckCircle2 size={13} className="text-[var(--color-primary)] flex-shrink-0" />
                            {item}
                          </div>
                        ))}
                        {cat.items.length > 4 && (
                          <div className="text-sm text-[var(--color-primary)]/70 pl-5">
                            +{cat.items.length - 4} more types
                          </div>
                        )}
                      </div>
                      <Link
                        href="/contact"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary)] group-hover:gap-2.5 transition-all"
                      >
                        Get Quote
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}

        {/* ===== TRAINING TAB ===== */}
        {activeTab === 'training' && (
          <section className="py-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Training Programs{' '}
                <span className="bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] bg-clip-text text-transparent">
                  for Every Level
                </span>
              </h2>
              <p className="text-[var(--color-muted)] max-w-2xl mx-auto">
                From complete beginners to advanced technicians. Our training programs are built on real-world repair data
                — not textbook theory.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-16">
              {[
                {
                  level: 'Level 1',
                  title: 'Basic EV Safety & Introduction',
                  duration: '3 Days',
                  description: 'Essential high-voltage safety training and EV system fundamentals. For workshop owners and technicians new to EV work.',
                  features: [
                    'High-voltage safety certification',
                    'EV system architecture overview',
                    'Personal protective equipment',
                    'Lockout / tagout procedures',
                    'Basic diagnostic tool usage',
                    'Safety regulations & standards',
                  ],
                  target: 'Workshop owners, new technicians',
                },
                {
                  level: 'Level 2',
                  title: 'EV Battery Service & Diagnostics',
                  duration: '7 Days',
                  description: 'Hands-on training in battery pack diagnosis, cell balancing, module replacement and common fault repair.',
                  features: [
                    'BMS data interpretation',
                    'Cell voltage balancing (passive + active)',
                    'Module replacement procedures',
                    'Common battery fault diagnosis',
                    'BYD Blade battery teardown',
                    'CATL NMC module service',
                    'Battery coolant service',
                    'Hands-on practice with real packs',
                  ],
                  target: 'Technicians with Level 1 or experience',
                  highlight: true,
                },
                {
                  level: 'Level 3',
                  title: 'Advanced Battery Reconditioning',
                  duration: '14 Days',
                  description: 'Our flagship program. Master battery reconditioning technology and build a high-margin battery repair business.',
                  features: [
                    'Cell grading & capacity testing',
                    'Battery pack rebuild techniques',
                    'Cell-to-pack replacement',
                    'BMS recalibration & coding',
                    'Advanced fault troubleshooting',
                    'Battery reconditioning equipment setup',
                    'Business model & pricing guidance',
                    'Marketing & customer acquisition',
                    'Ongoing technical support (1 year)',
                  ],
                  target: 'Serious workshop owners & technicians',
                },
              ].map((program) => (
                <div
                  key={program.level}
                  className={`relative p-6 rounded-2xl border transition-all hover:-translate-y-1 ${
                    program.highlight
                      ? 'bg-gradient-to-b from-[var(--color-primary)]/10 to-[var(--color-surface)] border-[var(--color-primary)]/40 shadow-xl shadow-[var(--color-primary)]/10'
                      : 'bg-[var(--color-surface)] border-[var(--color-border)] hover:border-[var(--color-primary)]/30'
                  }`}
                >
                  {program.highlight && (
                    <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[var(--color-primary)] text-[var(--color-background)] text-xs font-bold">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-sm font-bold ${program.highlight ? 'text-[var(--color-primary)]' : 'text-[var(--color-muted)]'}`}>
                      {program.level}
                    </span>
                    <span className="text-sm text-[var(--color-muted)]">{program.duration}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{program.title}</h3>
                  <p className="text-sm text-[var(--color-muted)] mb-5 leading-relaxed">{program.description}</p>
                  <div className="space-y-2 mb-6">
                    {program.features.map((feat) => (
                      <div key={feat} className="flex items-start gap-2 text-sm text-[var(--color-foreground)]">
                        <CheckCircle2 size={16} className="text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
                        {feat}
                      </div>
                    ))}
                  </div>
                  <div className="pt-5 border-t border-[var(--color-border)]">
                    <p className="text-xs text-[var(--color-muted)] mb-3">
                      <span className="font-medium text-[var(--color-foreground)]">For:</span> {program.target}
                    </p>
                    <Link
                      href="/contact"
                      className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold transition-all ${
                        program.highlight
                          ? 'text-[var(--color-background)] bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] hover:shadow-lg hover:shadow-[var(--color-glow-soft)]'
                          : 'text-[var(--color-primary)] border border-[var(--color-primary)]/30 hover:bg-[var(--color-primary)]/10'
                      }`}
                    >
                      <Wrench size={16} />
                      Request Training Info
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Why Train With Us */}
            <div className="p-8 md:p-12 rounded-3xl bg-[var(--color-surface)] border border-[var(--color-border)]">
              <h3 className="text-2xl font-bold mb-8 text-center">
                Why Workshops Choose WFD Training
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: GraduationCap, title: 'Expert Instructors', desc: 'Senior EV technicians with 10+ years of field experience' },
                  { icon: Wrench, title: 'Hands-On Practice', desc: '70% practical training on real Chinese EV battery packs' },
                  { icon: Shield, title: 'Certified Courses', desc: 'Industry-recognized certification upon completion' },
                  { icon: Activity, title: 'Ongoing Support', desc: '1 year of free technical support after training ends' },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="text-center">
                      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-accent)]/20 flex items-center justify-center">
                        <Icon size={28} className="text-[var(--color-primary)]" />
                      </div>
                      <h4 className="font-semibold mb-2">{item.title}</h4>
                      <p className="text-sm text-[var(--color-muted)]">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ===== Bottom CTA ===== */}
        <section className="py-16">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--color-primary)]/15 via-[var(--color-surface)] to-[var(--color-accent)]/15 border border-[var(--color-primary)]/20 p-8 md:p-12">
            <div className="absolute top-0 right-0 w-80 h-80 bg-[var(--color-primary)]/20 blur-[120px] rounded-full" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-[var(--color-accent)]/15 blur-[120px] rounded-full" />

            <div className="relative max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Bulk Pricing for Workshop Partners
              </h2>
              <p className="text-lg text-[var(--color-muted)] mb-8">
                We offer competitive tiered pricing for workshops and distributors.
                The more you buy, the better your margins. Start small — scale as you grow.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-[var(--color-background)] bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all"
                >
                  <Phone size={18} />
                  Get Wholesale Pricing
                </Link>
                <Link
                  href="/wholesale"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold border border-[var(--color-border)] hover:border-[var(--color-primary)]/50 hover:text-[var(--color-primary)] transition-all"
                >
                  Learn About Partnership
                  <ArrowRight size={18} />
                </Link>
              </div>
              <p className="text-sm text-[var(--color-muted)]/70 mt-6">
                No MOQ for first order • OEM quality verified • Fast shipping to Middle East & SEA
              </p>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
